import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { ColorPickerModule } from 'primeng/colorpicker';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { CurveStyleResult } from '../../../../../../types/graph-dialog.interface';
import { CesDialogFooterComponent } from '../../../../../ui/dialog-footer/dialog-footer.component';
import {
  COLOR_OPTIONS,
  COLOR_SWATCH_SIZE,
  DIALOG_MAX_WIDTH,
  DROPDOWN_PANEL_SIZES,
  DROPDOWN_SIZES,
  LINE_TYPE_OPTIONS,
  MARKER_SYMBOL_OPTIONS,
  THICKNESS_OPTIONS,
} from './curve-style.constants';
import type { ColorOption, LineType, MarkerSymbol } from './curve-style.types';

interface CurveStyleDialogData {
  currentStyle?: {
    name?: string;
    lineType?: LineType;
    color?: string;
    thickness?: number;
    markerSymbol?: MarkerSymbol;
  };
}

@Component({
  selector: 'app-curve-style-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, ColorPickerModule, SelectModule, InputTextModule, TranslocoModule, CesDialogFooterComponent],
  templateUrl: './curve-style-dialog.component.html',
  styleUrl: './curve-style-dialog.component.scss',
})
export class CurveStyleDialogComponent {
  private readonly ref = inject(DynamicDialogRef);
  private readonly config = inject<DynamicDialogConfig<CurveStyleDialogData>>(DynamicDialogConfig);
  private readonly translate = inject(TranslocoService);

  readonly lineTypeOptions = LINE_TYPE_OPTIONS;
  readonly markerSymbolOptions = MARKER_SYMBOL_OPTIONS;
  readonly colorOptions = computed<ColorOption[]>(() => [...COLOR_OPTIONS]);
  readonly thicknessOptions = THICKNESS_OPTIONS;
  readonly dropdownSizes = DROPDOWN_SIZES;
  readonly dropdownPanelSizes = DROPDOWN_PANEL_SIZES;
  readonly colorSwatchSize = COLOR_SWATCH_SIZE;
  readonly dialogMaxWidth = DIALOG_MAX_WIDTH;

  readonly name = signal('');
  readonly lineType = signal<LineType>('solid');
  readonly selectedColor = signal<string>(this.colorOptions()[0]?.value ?? '#000000');
  readonly thickness = signal(2);
  readonly markerSymbol = signal<MarkerSymbol>('circle');

  readonly colorOptionsWithCustom = computed(() => {
    const options = this.colorOptions();
    const currentColor = this.selectedColor();
    const isPredefined = options.some(c => c.value.toLowerCase() === currentColor.toLowerCase());

    if (isPredefined) {
      return options;
    }

    return [{ value: currentColor, labelKey: 'graphs.dialog.customColor' }, ...options];
  });

  readonly isValid = computed(() => {
    const currentName = this.name().trim();
    if (currentName.length === 0 || currentName.length > 100) return false;
    return /^[a-zA-Z0-9\s\-À-ÿ'°()._%/#+,]+$/.test(currentName);
  });

  getErrorMessageKey(): string {
    const currentName = this.name().trim();
    if (currentName.length === 0) return 'graphs.dialog.nameRequired';
    if (currentName.length > 100) return 'graphs.dialog.nameMaxLength';
    if (!/^[a-zA-Z0-9\s\-À-ÿ'°()._%/#+,]+$/.test(currentName)) return 'graphs.dialog.namePattern';
    return '';
  }

  getMarkerAriaLabel(): string {
    const option = this.markerSymbolOptions.find(o => o.value === this.markerSymbol());
    const labelKey = option?.labelKey ?? 'graphs.pointStyles.none';
    const label = this.translate.translate(labelKey);
    const prefix = this.translate.translate('graphs.dialog.markerAriaPrefix');
    return `${prefix} : ${label}`;
  }

  getLineTypeAriaLabel(): string {
    const option = this.lineTypeOptions.find(o => o.value === this.lineType());
    const labelKey = option?.labelKey ?? 'graphs.curveStyles.solid';
    const label = this.translate.translate(labelKey);
    const prefix = this.translate.translate('graphs.dialog.lineTypeAriaPrefix');
    return `${prefix} : ${label}`;
  }

  getThicknessAriaLabel(): string {
    return this.translate.translate('graphs.dialog.thicknessAriaLabel', { value: this.thickness() });
  }

  getColorAriaLabel(): string {
    const prefix = this.translate.translate('graphs.dialog.colorSelected');
    const option = this.colorOptions().find(o => o.value === this.selectedColor());
    const labelKey = option?.labelKey ?? 'graphs.colors.blue';
    const label = this.translate.translate(labelKey);
    return `${prefix} : ${label}`;
  }

  getValidateButtonAriaLabel(): string {
    return this.translate.translate(this.isValid() ? 'graphs.dialog.validateChangesAria' : 'graphs.dialog.invalidFormAria');
  }

  constructor() {
    const currentStyle = this.config.data?.currentStyle;
    if (!currentStyle) return;

    this.name.set(currentStyle.name ?? '');
    this.lineType.set(currentStyle.lineType ?? 'solid');
    this.selectedColor.set(currentStyle.color ?? this.colorOptions()[0]?.value ?? '#000000');
    this.thickness.set(currentStyle.thickness ?? 2);
    this.markerSymbol.set(currentStyle.markerSymbol ?? 'circle');
  }

  getLineDashArray(lineType: LineType): string {
    const patterns: Record<LineType, string> = {
      none: '',
      solid: '',
      dot: '2,4',
      dash: '8,4',
      longDash: '12,4',
      dashDot: '8,4,2,4',
      longDashDot: '12,4,2,4',
    };
    return patterns[lineType];
  }

  isLineNone(lineType: LineType): boolean {
    return lineType === 'none';
  }

  isMarkerNone(markerSymbol: MarkerSymbol): boolean {
    return markerSymbol === 'none';
  }

  cancel(): void {
    this.ref.close();
  }

  save(): void {
    if (!this.isValid()) return;

    const trimmedName = this.name().trim();
    const result: CurveStyleResult = {
      ...(trimmedName !== '' && { name: trimmedName }),
      lineType: this.lineType(),
      color: this.selectedColor(),
      thickness: this.thickness(),
      markerSymbol: this.markerSymbol(),
    };
    this.ref.close(result);
  }
}
